import {
  CommunityContactFormProps,
  EmailTemplate,
  FormFieldFields,
  FormFieldTypeField,
  FormFieldType,
  FormRow,
} from '@greystar/ui';
import { GraphQLRequestClient } from '@sitecore-jss/sitecore-jss-nextjs/graphql';
import createGraphQLClientFactory from '../../graphql-client-factory';
import { Field, Item } from '@sitecore-jss/sitecore-jss-nextjs';

/**
 * Represents a request to query a CommunityContactForm by its ID and language.
 */
export interface CommunityContactFormQueryRequest {
  country: string;
}

export interface CommunityContactFormQueryResponse {
  search?: {
    total: number;
    results: CommunityContactFormQueryResult[];
  };
}

export interface CommunityContactFormQueryResult {
  country: Field<string>;
  heading: Field<string>;
  description: Field<string>;
  emailOverride: Field<string>;
  errorMessage: Field<string>;
  formFooter: Field<string>;
  emailTemplates: {
    values: EmailTemplate[];
  };
  formFields: {
    values: GraphQLFormField[];
  };
}

type GraphQLFormField = FormFieldTypeField &
  Item & { rowFields?: { values: FormFieldTypeField & Item[] } };

const convertGraphQLResultToProps = (
  result: CommunityContactFormQueryResult
): Omit<CommunityContactFormProps, 'rendering' | 'params' | 'data'> => {
  const formFields = result.formFields.values.map((field) =>
    convertGraphQLFormFieldToPropsFormField(field as GraphQLFormField)
  ) as unknown as FormFieldType[];

  return {
    fields: {
      ...result,
      emailTemplates: result.emailTemplates.values,
      formFields,
    },
  };
};

const convertGraphQLFormFieldToPropsFormField = (field: GraphQLFormField): FormFieldType & Item => {
  if ((field as FormFieldFields).fieldType.value === 'row') {
    return {
      id: field.id,
      name: field.name,
      fields: {
        fieldType: (field as FormFieldFields).fieldType,
        rowFields: field.rowFields?.values?.map(
          (field) =>
            ({ id: field.id, name: field.name, fields: { ...field } } as unknown as FormFieldType)
        ),
      },
    } as FormRow;
  } else {
    return { id: field.id, name: field.name, fields: { ...field } } as unknown as FormFieldType;
  }
};

/**
 * Fetches CommunityContactForm based on the provided request.
 *
 * @param {GraphQLRequestClient} client - The GraphQL client to use for the request.
 * @param {CommunityContactFormQueryRequest} request - The request containing the CommunityContactForm ID and language.
 * @returns {Promise<CommunityContactFormResponse | null>} A promise that resolves to the CommunityContactForm response or null if the request fails.
 */
export const getCommunityContactForm = async ({
  client,
  request,
}: {
  client?: GraphQLRequestClient;
  request: CommunityContactFormQueryRequest;
}): Promise<CommunityContactFormProps | null> => {
  const graphQLClient: GraphQLRequestClient = client ?? createGraphQLClientFactory();
  const forms = await graphQLClient
    .request<CommunityContactFormQueryResponse>(GetCommunityContactFormsQuery, {
      country: request.country,
    })
    .catch((error) => {
      console.error(`Failed to get CommunityContactForm for country: ${request.country}`, error);
      return null;
    });

  const results = forms?.search?.results?.filter(
    (result) => result.emailTemplates.values.length > 0
  );
  const result =
    results?.find(
      (form) =>
        form.country?.value?.trim()?.toLocaleLowerCase() ===
        request.country?.trim()?.toLocaleLowerCase()
    ) ??
    results?.find((form) => !form.country?.value) ??
    null;

  if (result == null) {
    return null;
  }

  return convertGraphQLResultToProps(result) as unknown as CommunityContactFormProps;
};

/**
 * The GraphQL query to fetch the URL of an item.
 */
const GetCommunityContactFormsQuery = /* GraphQL */ `
  fragment _FormField on Item {
    id
    name
    ... on _FormField {
      fieldType {
        value
      }
      fieldKey {
        value
      }
      mandatory {
        value: boolValue
      }
      label {
        value
      }
      hint {
        value
      }
      description {
        value
      }
      errorMessage {
        value
      }
    }
  }

  fragment FormFieldCheckbox on FormFieldCheckbox {
    richLabel {
      value
    }
  }

  fragment FormFieldCheckboxes on FormFieldCheckboxes {
    atLeastOneChecked {
      value: boolValue
    }
    checkboxes {
      targetItems {
        ...FormFieldCheckbox
      }
    }
  }

  fragment FormFieldDatePicker on FormFieldDatePicker {
    monthLabel {
      value
    }
    monthFieldKey {
      value
    }
    dayLabel {
      value
    }
    dayFieldKey {
      value
    }
    yearLabel {
      value
    }
    yearFieldKey {
      value
    }
    minDate {
      value
    }
    maxDate {
      value
    }
  }

  fragment FormFieldDropdown on FormFieldDropdown {
    placeholder {
      value
    }
    defaultValue {
      value
    }
    showValue {
      value: boolValue
    }
    options {
      jsonValue
    }
  }

  fragment FormFieldRadio on FormFieldRadio {
    defaultValue {
      value
    }
    options {
      jsonValue
    }
  }

  fragment FormFieldText on FormFieldText {
    placeholder {
      value
    }
    regex {
      value
    }
  }

  fragment FormFieldTextArea on FormFieldTextArea {
    placeholder {
      value
    }
    regex {
      value
    }
  }

  fragment FormFieldType on Item {
    ..._FormField
    ...FormFieldCheckbox
    ...FormFieldCheckboxes
    ...FormFieldDatePicker
    ...FormFieldDropdown
    ...FormFieldRadio
    ...FormFieldText
    ...FormFieldTextArea
  }

  fragment FormFieldRow on FormFieldRow {
    fieldType {
      value
    }
    rowFields {
      values: targetItems {
        ...FormFieldType
      }
    }
  }

  query CommunityContactForm {
    search(
      where: {
        AND: [
          { name: "_path", value: "{8710F6F6-749F-4F3C-8B1C-CB3A2D7E079C}", operator: CONTAINS }
          { name: "_templates", value: "{771B7C63-F342-4059-86A7-F2F38B09A4B3}" }
        ]
      }
    ) {
      results {
        ... on CommunityContactForm {
          country {
            value
          }
          heading {
            value
          }
          description {
            value
          }
          emailOverride {
            value
          }

          errorMessage {
            value
          }
          formFooter {
            value
          }
          emailTemplates {
            values: jsonValue
          }
          formFields {
            values: targetItems {
              ...FormFieldType
              ...FormFieldRow
            }
          }
        }
      }
    }
  }
`;
